// QGIS-style display controls for rows in the Layer Info dialog.
// Kept separate so this feature can be deployed independently of the core layer tree.

import CircleStyle from 'ol/style/Circle.js';
import Fill from 'ol/style/Fill.js';
import RegularShape from 'ol/style/RegularShape.js';
import Stroke from 'ol/style/Stroke.js';

function parseBackendStyle(layer) {
  try {
    const value = layer?.get?.('backendStyle');
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function toHexColor(value, fallback) {
  if (typeof value !== 'string') return fallback;
  if (/^#[0-9a-f]{6}$/i.test(value)) return value;
  if (/^#[0-9a-f]{8}$/i.test(value)) return value.slice(0, 7);
  const match = value.match(/^rgba?\(\s*(\d+)\D+(\d+)\D+(\d+)/i);
  if (!match) return fallback;
  return `#${match.slice(1, 4).map(part => Number(part).toString(16).padStart(2, '0')).join('')}`;
}

function getColorAlpha(value) {
  if (typeof value !== 'string') return 1;
  const hexMatch = value.match(/^#[0-9a-f]{6}([0-9a-f]{2})$/i);
  if (hexMatch) return parseInt(hexMatch[1], 16) / 255;
  const rgbaMatch = value.match(/^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([0-9]*\.?[0-9]+)\s*\)$/i);
  return rgbaMatch ? Math.min(1, Math.max(0, Number(rgbaMatch[1]))) : 1;
}

function withAlpha(hexColor, alpha = 1) {
  const match = hexColor.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!match) return hexColor;
  return `rgba(${parseInt(match[1], 16)}, ${parseInt(match[2], 16)}, ${parseInt(match[3], 16)}, ${alpha})`;
}

function getLayerGeometryKind(layer, backend, isPoint) {
  const feature = layer?.getSource?.()?.getFeatures?.()?.[0];
  const geometryType = feature?.getGeometry?.()?.getType?.();
  if (['Point', 'MultiPoint'].includes(geometryType) || isPoint) return 'point';
  if (['LineString', 'MultiLineString'].includes(geometryType)) return 'line';
  if (['Polygon', 'MultiPolygon', 'Circle'].includes(geometryType)) return 'polygon';
  if (backend?.stroke && !backend?.fill && !backend?.image) return 'line';
  const sourceName = layer?.getSource?.()?.constructor?.name || '';
  if (/Tile|Image|Raster/i.test(sourceName)) return 'raster';
  return 'polygon';
}

function getEditableStyle(layer) {
  const backend = parseBackendStyle(layer);
  if (!backend && layer?.get?.('poiSetId')) {
    return {
      fillColor: toHexColor(layer.get('poiStyleColor'), '#0d6efd'),
      fillAlpha: 1,
      strokeColor: '#ffffff',
      strokeWidth: 2,
      pointRadius: 7,
      pointShape: 'circle',
      isPoint: true,
      geometryKind: 'point'
    };
  }
  if (!backend) {
    return {
      fillColor: '#e5eadf', fillAlpha: 1, strokeColor: '#b8c3ae', strokeWidth: 1,
      pointRadius: 5, pointShape: 'circle', isPoint: false, geometryKind: getLayerGeometryKind(layer, null, false)
    };
  }
  const image = backend.image || {};
  const semanticRole = String(layer?.get?.('semanticRole') || '').toLowerCase();
  const isKnownAreaLayer = ['pc4', 'pc5', 'pc6'].includes(semanticRole);
  const isKnownPointLayer = semanticRole === 'address';
  const isPoint = isKnownPointLayer || (
    !isKnownAreaLayer &&
    !!backend.image &&
    !backend.fill &&
    !backend.stroke
  );
  const fillColor = isPoint
    ? image.fill?.color || backend.fill?.color
    : backend.fill?.color || image.fill?.color;
  const strokeColor = isPoint
    ? image.stroke?.color || backend.stroke?.color
    : backend.stroke?.color || image.stroke?.color;
  const strokeWidth = isPoint
    ? image.stroke?.width || backend.stroke?.width
    : backend.stroke?.width || image.stroke?.width;
  return {
    fillColor: toHexColor(fillColor, '#7e8ce0'),
    fillAlpha: getColorAlpha(fillColor),
    strokeColor: toHexColor(strokeColor, '#3b39e8'),
    strokeWidth: Number(strokeWidth || 1.5),
    pointRadius: Number(image.radius || 5),
    pointShape: 'circle',
    isPoint,
    geometryKind: getLayerGeometryKind(layer, backend, isPoint)
  };
}

function createPointImage(values) {
  const common = {
    radius: values.pointRadius,
    fill: new Fill({ color: withAlpha(values.fillColor, values.fillAlpha) }),
    stroke: new Stroke({ color: values.strokeColor, width: values.strokeWidth })
  };

  const shapes = {
    square: { points: 4, angle: Math.PI / 4 },
    triangle: { points: 3, angle: 0 },
    diamond: { points: 4, angle: 0 },
    pentagon: { points: 5, angle: 0 },
    hexagon: { points: 6, angle: 0 },
    star: { points: 5, radius2: Math.max(1, values.pointRadius * 0.45), angle: 0 },
    plus: { points: 4, radius2: 0, angle: 0 },
    x: { points: 4, radius2: 0, angle: Math.PI / 4 }
  };

  return shapes[values.pointShape]
    ? new RegularShape({ ...common, ...shapes[values.pointShape] })
    : new CircleStyle(common);
}

function rememberInitialStyle(layer) {
  if (layer.get('initialDisplayStyleCaptured')) return;
  layer.set('initialDisplayStyle', layer.getStyle?.());
  layer.set('initialDisplayOpacity', layer.getOpacity?.() ?? 1);
  layer.set('initialDisplayStyleCaptured', true);
}

function isPointFeature(feature) {
  const geometryType = feature?.getGeometry?.()?.getType?.();
  return geometryType === 'Point' || geometryType === 'MultiPoint';
}

function isAreaGeometry(geometry) {
  const geometryType = geometry?.getType?.();
  if (geometryType === 'Polygon' || geometryType === 'MultiPolygon' || geometryType === 'Circle') {
    return true;
  }
  if (geometryType === 'GeometryCollection') {
    return geometry.getGeometries?.().some(isAreaGeometry) || false;
  }
  return false;
}

function applyLayerStyleOverride(layer, values) {
  rememberInitialStyle(layer);
  const initialStyle = layer.get('initialDisplayStyle');
  const initialStyleFunction = typeof initialStyle === 'function'
    ? initialStyle
    : layer.getStyleFunction?.();
  if (!initialStyleFunction) return;

  layer.setStyle((feature, resolution) => {
    const rendered = initialStyleFunction(feature, resolution);
    const styles = Array.isArray(rendered) ? rendered : [rendered];
    const areaFeature = isAreaGeometry(feature?.getGeometry?.());
    const edited = styles.filter(Boolean).map(style => {
      const clone = style.clone();
      const image = clone.getImage?.();
      if (values.isPoint && isPointFeature(feature) && clone.setImage) {
        clone.setImage(createPointImage(values));
        return clone;
      }
      if (areaFeature || clone.getFill?.()) {
        clone.setFill(new Fill({ color: withAlpha(values.fillColor, values.fillAlpha) }));
      }
      if (clone.getStroke?.()) {
        const currentStroke = clone.getStroke();
        clone.setStroke(new Stroke({
          color: values.strokeColor,
          width: values.strokeWidth,
          lineCap: currentStroke.getLineCap?.(),
          lineJoin: currentStroke.getLineJoin?.(),
          lineDash: currentStroke.getLineDash?.()
        }));
      }
      if (image?.setRadius) image.setRadius(values.pointRadius);
      if (image?.setFill) {
        image.setFill(new Fill({ color: withAlpha(values.fillColor, values.fillAlpha) }));
      }
      if (image?.setStroke) {
        image.setStroke(new Stroke({ color: values.strokeColor, width: values.strokeWidth }));
      }
      return clone;
    });
    return Array.isArray(rendered) ? edited : edited[0];
  });
  layer.changed?.();
}

export function createQgisLayerControls(layer, refreshLayerPanel = () => {}) {
  const backendStyle = getEditableStyle(layer);
  const userStyle = layer.get('userDisplayStyle');
  const editable = userStyle ? { ...backendStyle, ...userStyle } : backendStyle;
  const details = document.createElement('div');
  details.className = 'layer-legend-details';
  if (!editable) return details;

  rememberInitialStyle(layer);
  const values = { ...editable };

  if (layer?.get?.('poiSetId')) {
    details.classList.add('poi-legend-details');
    const iconColor = document.createElement('span');
    iconColor.className = 'poi-legend-icon-color';
    iconColor.style.setProperty('--poi-legend-color', values.fillColor);
    iconColor.title = `Category color ${values.fillColor}`;
    iconColor.setAttribute('aria-label', iconColor.title);
    details.appendChild(iconColor);
    return details;
  }

  const symbol = document.createElement('span');
  symbol.className = `layer-legend-symbol is-${values.geometryKind || (values.isPoint ? 'point' : 'polygon')}`;
  symbol.style.setProperty('--legend-fill', withAlpha(values.fillColor, values.fillAlpha));
  symbol.style.setProperty('--legend-stroke', values.strokeColor);
  symbol.title = `Fill ${values.fillColor}; outline ${values.strokeColor}`;
  symbol.setAttribute('aria-label', symbol.title);

  details.appendChild(symbol);
  return details;

  const addColorField = (labelText, key) => {
    const label = document.createElement('label');
    label.className = 'layer-style-field layer-color-field';
    label.title = labelText;
    label.innerHTML = `<span>${labelText}</span><input aria-label="${labelText}" type="color" value="${values[key]}">`;
    label.querySelector('input').addEventListener('input', event => {
      values[key] = event.currentTarget.value;
      layer.set('userDisplayStyle', { ...values });
      applyLayerStyleOverride(layer, values);
    });
    details.appendChild(label);
  };
  addColorField('Fill', 'fillColor');
  addColorField('Outline', 'strokeColor');

  if (values.isPoint) {
    const shape = document.createElement('label');
    shape.className = 'layer-style-field layer-shape-field';
    shape.title = 'Point symbol';
    shape.innerHTML = `
      <span>Point symbol</span>
      <select aria-label="Point symbol">
        <option value="circle">●</option><option value="square">■</option>
        <option value="triangle">▲</option><option value="diamond">◆</option>
        <option value="pentagon">⬟</option><option value="hexagon">⬢</option>
        <option value="star">★</option><option value="plus">✚</option>
        <option value="x">✖</option>
      </select>`;
    const select = shape.querySelector('select');
    select.value = values.pointShape;
    select.addEventListener('change', event => {
      values.pointShape = event.currentTarget.value;
      layer.set('userDisplayStyle', { ...values });
      applyLayerStyleOverride(layer, values);
    });
    details.appendChild(shape);

    const radius = document.createElement('label');
    radius.className = 'layer-style-field';
    radius.title = 'Point size';
    radius.innerHTML = `<span>Size</span><input aria-label="Point size" type="number" min="1" max="40" step="1" value="${values.pointRadius}">`;
    radius.querySelector('input').addEventListener('input', event => {
      values.pointRadius = Math.max(1, Number(event.currentTarget.value) || 1);
      layer.set('userDisplayStyle', { ...values });
      applyLayerStyleOverride(layer, values);
    });
    details.appendChild(radius);
  } else {
    const width = document.createElement('label');
    width.className = 'layer-style-field';
    width.title = 'Line width';
    width.innerHTML = `<span>Width</span><input aria-label="Line width" type="number" min="0" max="12" step="0.5" value="${values.strokeWidth}">`;
    width.querySelector('input').addEventListener('input', event => {
      values.strokeWidth = Math.max(0, Number(event.currentTarget.value) || 0);
      layer.set('userDisplayStyle', { ...values });
      applyLayerStyleOverride(layer, values);
    });
    details.appendChild(width);
  }

  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'btn btn-sm btn-outline-secondary layer-style-reset';
  reset.innerHTML = '<i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>';
  reset.title = 'Reset backend style';
  reset.setAttribute('aria-label', 'Reset backend style');
  reset.addEventListener('click', () => {
    layer.setStyle(layer.get('initialDisplayStyle'));
    layer.setOpacity(layer.get('initialDisplayOpacity') ?? 1);
    layer.unset('userDisplayStyle');
    layer.changed?.();
    refreshLayerPanel();
  });
  details.appendChild(reset);
  return details;
}
